import React from 'react';
import { Link } from 'react-router';
import type { LscVodCandidate } from '../types';
import VerificationStatusBadge from './VerificationStatusBadge';
import ReviewStatusBadge from './ReviewStatusBadge';
import { lscPath } from '../routing';
import {
  getVodInventoryDiscoveryBadges,
  vodInventoryDiscoveryBadgeStyle,
} from '../vodTruth';

interface VodCandidatesTableProps {
  leagueId: string;
  candidates: LscVodCandidate[];
  emptyMessage?: string;
}

function formatDuration(seconds?: number | null, raw?: string): string {
  if (raw) return raw;
  if (seconds == null) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

const VodCandidatesTable: React.FC<VodCandidatesTableProps> = ({
  leagueId,
  candidates,
  emptyMessage,
}) => {
  if (!candidates.length) {
    return (
      <p className="text-secondary-400 text-sm" data-testid="vod-candidates-empty">
        {emptyMessage ?? 'No VOD candidates yet. Run a sync to discover Twitch VOD metadata.'}
      </p>
    );
  }

  return (
    <div
      className="overflow-x-auto rounded-lg border border-secondary-700"
      data-testid="vod-candidates-table"
    >
      <table className="min-w-full text-sm">
        <thead className="bg-secondary-800 text-secondary-300">
          <tr>
            <th className="px-4 py-3 text-left font-medium">Title</th>
            <th className="px-4 py-3 text-left font-medium">Broadcaster</th>
            <th className="px-4 py-3 text-left font-medium">Created</th>
            <th className="px-4 py-3 text-left font-medium">Duration</th>
            <th className="px-4 py-3 text-left font-medium">Labels</th>
            <th className="px-4 py-3 text-left font-medium">Match</th>
            <th className="px-4 py-3 text-left font-medium">Review</th>
            <th className="px-4 py-3 text-left font-medium">Archive</th>
            <th className="px-4 py-3 text-left font-medium">Verification</th>
            <th className="px-4 py-3 text-left font-medium" />
          </tr>
        </thead>
        <tbody className="divide-y divide-secondary-700">
          {candidates.map((candidate) => {
            const badges = getVodInventoryDiscoveryBadges(candidate);
            return (
              <tr key={candidate.id} className="hover:bg-secondary-800/50">
                <td className="px-4 py-3 text-white max-w-xs truncate">
                  {candidate.title ?? 'Untitled'}
                </td>
                <td className="px-4 py-3 text-secondary-300">
                  {candidate.provider_broadcaster_login
                    ? `@${candidate.provider_broadcaster_login}`
                    : '—'}
                </td>
                <td className="px-4 py-3 text-secondary-300">
                  {candidate.created_at
                    ? new Date(candidate.created_at).toLocaleDateString()
                    : '—'}
                </td>
                <td className="px-4 py-3 text-secondary-300">
                  {formatDuration(candidate.duration_seconds, candidate.raw_duration)}
                </td>
                <td className="px-4 py-3">
                  {badges.length > 0 ? (
                    <div className="flex flex-wrap gap-1" data-testid="vod-candidate-labels">
                      {badges.map((badge) => (
                        <span
                          key={badge.key}
                          className={`inline-flex rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${vodInventoryDiscoveryBadgeStyle(badge.tone)}`}
                        >
                          {badge.label}
                        </span>
                      ))}
                    </div>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-4 py-3 capitalize text-secondary-300">
                  {candidate.match_status ?? 'unmatched'}
                </td>
                <td className="px-4 py-3">
                  <ReviewStatusBadge status={candidate.review_status} />
                </td>
                <td className="px-4 py-3 text-secondary-300">
                  {candidate.archive_id ? (
                    <Link
                      to={lscPath(
                        `/leagues/${leagueId}/archive/${encodeURIComponent(candidate.archive_id)}`
                      )}
                      className="text-primary-300 hover:text-primary-200 text-xs"
                    >
                      {candidate.archive_status ?? 'View'}
                    </Link>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-4 py-3">
                  <VerificationStatusBadge candidate={candidate} />
                </td>
                <td className="px-4 py-3">
                  <Link
                    to={lscPath(`/leagues/${leagueId}/vods/${encodeURIComponent(candidate.id)}`)}
                    className="text-primary-300 hover:text-primary-200 text-sm"
                  >
                    View
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default VodCandidatesTable;
